import type { RuntimeTaskResult } from "@wakeloop/local-execution";
import {
  SPACE_ACTION_CONTEXT_GUIDANCE_LINES,
  SPACE_ACTION_TARGET_GUIDANCE_LINES,
} from "@wakeloop/local-service";
import {
  wakeLoopDaemonTaskResultPayloadSchema,
  wakeLoopSpaceEgressActionSchema,
} from "@wakeloop/schemas";
import type {
  AgentControllerConversationRef,
  WakeLoopDaemonTaskResultPayload,
} from "@wakeloop/schemas/types";
import {
  hydrateWakeLoopSpaceEgressActionInputFromCleanedOutput,
  normalizeWakeLoopSpaceEgressActionInput,
  resolvePublicAgentFailureReason,
  resolveSpacePublicationRequirement,
  tryParseStructuredSpaceActionBlockInput,
} from "@wakeloop/space-mesh";
import { normalizeOptionalText } from "../core/logger";
import {
  normalizeOptionalContextId,
  resolveDispatchContextDeltaFromRuntimeResult,
} from "./execute-dispatch-conversation";
import type {
  ParsedDispatchTask,
  StructuredActionFailureDiagnostics,
} from "./execute-dispatch-types";

const STRUCTURED_SPACE_ACTION_BLOCK_HEADER_REGEX = /^\s*\r?\n?/u;

export function resolveStructuredActionRequirementFailure(input: {
  requiresStructuredSpaceAction: boolean;
  resolution: {
    action: WakeLoopDaemonTaskResultPayload["spaceAction"];
    parseError: string | null;
  };
  runtimeResult: RuntimeTaskResult;
}):
  | {
      failureReason: string;
      kind: "preserve_runtime_failure";
    }
  | {
      failureReason: string;
      kind: "repair";
    }
  | null {
  if (input.resolution.parseError) {
    return {
      failureReason: input.resolution.parseError,
      kind: "repair",
    };
  }
  if (!(input.requiresStructuredSpaceAction && !input.resolution.action)) {
    return null;
  }

  const payload = input.runtimeResult.resultPayload;
  if (payload.status === "failed") {
    return {
      failureReason:
        resolvePublicAgentFailureReason({
          defaultReason: "The agent could not complete this reply right now.",
          errorCode: payload.errorCode,
          errorMessage: payload.errorMessage,
        }) ?? "The agent could not complete this reply right now.",
      kind: "preserve_runtime_failure",
    };
  }

  return {
    failureReason:
      "A valid WakeLoop Space action is required in collaboration mode.",
    kind: "repair",
  };
}

export function resolveStructuredActionOutputFallback(input: {
  action: NonNullable<WakeLoopDaemonTaskResultPayload["spaceAction"]>;
  cleanedOutput: string;
}): string {
  const cleanedOutput = input.cleanedOutput.trim();
  if (cleanedOutput.length > 0) {
    return cleanedOutput;
  }

  switch (input.action.type) {
    case "post_message":
      return input.action.messageEnvelope.text;
    case "post_status":
      return input.action.text.trim() || `status:${input.action.status}`;
    case "stay_silent":
      return input.action.reason.trim() || "stay_silent";
    default:
      return "structured-action";
  }
}

export function buildStructuredActionRepairPrompt(input: {
  cliEntry?: string | null;
  currentReplyToSignalId?: string | null;
  parseError: string;
  sendTargetReferenceLines?: readonly string[] | null;
  spaceContractSnapshot?: ParsedDispatchTask["spaceContractSnapshot"];
  spaceId?: string | null;
  previousOutput?: string | null;
}): string {
  const previousOutput = normalizeOptionalText(input.previousOutput);
  const normalizedSpaceId = normalizeOptionalText(input.spaceId);
  const cliEntry = normalizeOptionalText(input.cliEntry) ?? "wakeloop";
  const historyCommand = normalizedSpaceId
    ? `${cliEntry} space history ${normalizedSpaceId} --kind text --limit 20`
    : `${cliEntry} space history <space-id> --kind text --limit 20`;
  const promptCommand = `${cliEntry} prompt`;
  const spaceGuideCommand = normalizedSpaceId
    ? `${cliEntry} space guide ${normalizedSpaceId}`
    : `${cliEntry} space guide <space-id>`;
  const publicationRequirement = resolveSpacePublicationRequirement(
    input.spaceContractSnapshot
  );
  return [
    "Your previous WakeLoop Space structured action was invalid.",
    `Problem: ${input.parseError}`,
    ...(previousOutput
      ? ["Your previous final answer was:", "```text", previousOutput, "```"]
      : []),
    "This is a bounded repair of the public delivery action only. Do not redo the user's work.",
    "Do not change the user's intent.",
    ...SPACE_ACTION_CONTEXT_GUIDANCE_LINES,
    "WakeLoop only consumes your final answer and at most one valid trailing ```wakeloop-space-action``` JSON block.",
    "If a WakeLoop Space Action CLI command is available, run that command instead of printing fallback JSON.",
    publicationRequirement.repairRequirementLine,
    ...SPACE_ACTION_TARGET_GUIDANCE_LINES,
    ...(normalizeOptionalText(input.currentReplyToSignalId)
      ? [
          `post_message.messageEnvelope.replyToSignalId must match the current dispatch reply target: ${normalizeOptionalText(input.currentReplyToSignalId)}.`,
        ]
      : []),
    ...publicationRequirement.actionSchemaGuidanceLines,
    ...buildStructuredActionRepairSendTargetReferenceLines(
      input.sendTargetReferenceLines
    ),
    `If you need recent public room context, use \`${historyCommand}\`.`,
    `If you need the WakeLoop action rules again, read the official guidance with \`${promptCommand}\` or \`${spaceGuideCommand}\`.`,
    ...(normalizedSpaceId
      ? [
          `For this room, you can inspect the current guide with \`${spaceGuideCommand}\`.`,
        ]
      : []),
    ...buildStructuredActionRepairCompletionLines({
      cliEntry,
      publicationRequirement,
    }),
  ].join("\n");
}

function buildStructuredActionRepairCompletionLines(input: {
  cliEntry: string;
  publicationRequirement: ReturnType<typeof resolveSpacePublicationRequirement>;
}): string[] {
  return [
    "Repair completion rule:",
    buildStructuredActionRepairPrimaryCommandLine(input),
    "If the command cannot run, finish with the corrected public answer followed by exactly one valid trailing ```wakeloop-space-action``` block.",
    "Do not finish with an explanation of what you will do.",
  ];
}

function buildStructuredActionRepairPrimaryCommandLine(input: {
  cliEntry: string;
  publicationRequirement: ReturnType<typeof resolveSpacePublicationRequirement>;
}): string {
  if (input.publicationRequirement.allowedActions.includes("post_message")) {
    return `For a normal reply, run \`${input.cliEntry} space action reply "corrected public message"\` now and wait for it to finish.`;
  }
  if (input.publicationRequirement.allowedActions.includes("post_status")) {
    return `For a final status, run \`${input.cliEntry} space action status done "short completion update"\` now and wait for it to finish.`;
  }
  if (input.publicationRequirement.allowedActions.includes("stay_silent")) {
    return `If no public reply is needed, run \`${input.cliEntry} space action silent --reason "reason"\` now and wait for it to finish.`;
  }
  return `Run \`${input.cliEntry} prompt space-action --raw\` now and return one valid trailing wakeloop-space-action block.`;
}

function buildStructuredActionRepairSendTargetReferenceLines(
  sendTargetReferenceLines: readonly string[] | null | undefined
): string[] {
  if (!(sendTargetReferenceLines && sendTargetReferenceLines.length > 0)) {
    return [];
  }

  return [
    "Current send target reference:",
    ...sendTargetReferenceLines.map((line) => line),
  ];
}

export function resolveStructuredActionRepairPreviousOutput(
  runtimeResult: RuntimeTaskResult
): string | null {
  const payload = runtimeResult.resultPayload;
  if (payload.status !== "success" && payload.status !== "partial_success") {
    return null;
  }
  return normalizeOptionalText(payload.output);
}

export function resolveStructuredActionRepairContinuation(input: {
  runtimeResult: RuntimeTaskResult;
  agentControllerConversationOverride?: AgentControllerConversationRef | null;
}): {
  contextId: string | null;
  agentControllerConversationOverride: AgentControllerConversationRef | null;
} {
  const contextDelta = resolveDispatchContextDeltaFromRuntimeResult(
    input.runtimeResult.dispatchResult
  );
  return {
    contextId:
      normalizeOptionalContextId(
        input.runtimeResult.contextResolution?.agentControllerConversationId ??
          contextDelta?.agentControllerConversationId ??
          null
      ) ?? null,
    agentControllerConversationOverride:
      input.runtimeResult.contextResolution?.agentControllerConversation ??
      contextDelta?.agentControllerConversation ??
      input.agentControllerConversationOverride ??
      null,
  };
}

export function buildSyntheticStructuredSpaceActionRepairResult(input: {
  runtimeResult: RuntimeTaskResult;
}): RuntimeTaskResult | null {
  const payload = input.runtimeResult.resultPayload;
  if (payload.status !== "success" && payload.status !== "partial_success") {
    return null;
  }

  const repairedAction = attemptLocalStructuredSpaceActionRepair(
    payload.output
  );
  if (!repairedAction) {
    return null;
  }

  const candidate = resolveTrailingStructuredSpaceActionCandidate(
    payload.output
  );
  return {
    ...input.runtimeResult,
    resultPayload: {
      ...payload,
      output: resolveStructuredActionOutputFallback({
        action: repairedAction,
        cleanedOutput: candidate?.cleanedOutput ?? payload.output,
      }),
      spaceAction: repairedAction,
    },
  };
}

function attemptLocalStructuredSpaceActionRepair(
  output: string
): WakeLoopDaemonTaskResultPayload["spaceAction"] {
  const candidate = resolveTrailingStructuredSpaceActionCandidate(output);
  if (!candidate) {
    return undefined;
  }
  const parsedBlock = tryParseStructuredSpaceActionBlockInput(
    candidate.blockText
  );
  if (!(parsedBlock.actionInput && !parsedBlock.parseError)) {
    return undefined;
  }
  const normalizedAction = normalizeWakeLoopSpaceEgressActionInput(
    hydrateWakeLoopSpaceEgressActionInputFromCleanedOutput({
      action: parsedBlock.actionInput,
      cleanedOutput: candidate.cleanedOutput,
    })
  );
  const parsedAction =
    wakeLoopSpaceEgressActionSchema.safeParse(normalizedAction);
  if (!parsedAction.success) {
    return undefined;
  }
  return parsedAction.data;
}

function resolveTrailingStructuredSpaceActionCandidate(output: string): {
  blockText: string;
  cleanedOutput: string;
} | null {
  const normalizedOutput = typeof output === "string" ? output : "";
  const marker = "```wakeloop-space-action";
  const markerIndex = normalizedOutput.lastIndexOf(marker);
  if (markerIndex < 0) {
    return null;
  }

  const afterMarker = normalizedOutput.slice(markerIndex + marker.length);
  const headerMatch =
    STRUCTURED_SPACE_ACTION_BLOCK_HEADER_REGEX.exec(afterMarker);
  const blockStartOffset = headerMatch?.[0].length ?? 0;
  let blockText = afterMarker.slice(blockStartOffset).trim();
  if (blockText.endsWith("```")) {
    blockText = blockText.slice(0, -3).trimEnd();
  }
  if (blockText.length === 0) {
    return null;
  }

  return {
    blockText,
    cleanedOutput: normalizedOutput.slice(0, markerIndex).trimEnd(),
  };
}

export function buildInvalidStructuredActionOutputPreview(
  runtimeResult: RuntimeTaskResult
): string | null {
  const payload = runtimeResult.resultPayload;
  if (payload.status !== "success" && payload.status !== "partial_success") {
    return null;
  }
  const normalizedOutput = normalizeOptionalText(payload.output);
  if (!normalizedOutput) {
    return null;
  }
  if (normalizedOutput.length <= 180) {
    return normalizedOutput;
  }
  return `${normalizedOutput.slice(0, 177).trimEnd()}...`;
}

export function coerceRuntimeResultToStructuredActionFailure(input: {
  diagnostics?: StructuredActionFailureDiagnostics | null;
  failureReason: string;
  runtimeResult: RuntimeTaskResult;
}): WakeLoopDaemonTaskResultPayload {
  const payload = input.runtimeResult.resultPayload;
  const normalizedDiagnostics = input.diagnostics
    ? {
        ...input.diagnostics,
        publicFailureReason:
          input.diagnostics.publicFailureReason ??
          normalizeOptionalText(input.failureReason),
      }
    : null;
  const normalizedPayload = wakeLoopDaemonTaskResultPayloadSchema.safeParse({
    status: "failed",
    attemptId: payload.attemptId,
    taskId: payload.taskId,
    sourceEventId: payload.sourceEventId,
    agentId: payload.agentId,
    deduped: payload.deduped,
    ...(payload.agentControllerRef
      ? { agentControllerRef: payload.agentControllerRef }
      : {}),
    ...(payload.agentControllerConversationId
      ? { agentControllerConversationId: payload.agentControllerConversationId }
      : {}),
    ...(payload.telemetry ? { telemetry: payload.telemetry } : {}),
    ...(payload.agentControllerConversation
      ? { agentControllerConversation: payload.agentControllerConversation }
      : {}),
    ...(payload.bootstrapTemplateFacts
      ? { bootstrapTemplateFacts: payload.bootstrapTemplateFacts }
      : {}),
    ...(payload.dispatchExecution
      ? { dispatchExecution: payload.dispatchExecution }
      : {}),
    ...(normalizedDiagnostics
      ? { spaceActionFailureDiagnostics: normalizedDiagnostics }
      : {}),
    errorCode: "wakeloop.space_action.repair_failed",
    errorMessage: input.failureReason,
    errorType: "policy",
  });
  if (normalizedPayload.success) {
    return normalizedPayload.data;
  }

  return {
    status: "failed",
    attemptId: payload.attemptId,
    taskId: payload.taskId,
    sourceEventId: payload.sourceEventId,
    agentId: payload.agentId,
    deduped: payload.deduped,
    ...(payload.agentControllerRef
      ? { agentControllerRef: payload.agentControllerRef }
      : {}),
    ...(normalizedDiagnostics
      ? { spaceActionFailureDiagnostics: normalizedDiagnostics }
      : {}),
    errorCode: "wakeloop.space_action.repair_failed",
    errorMessage: input.failureReason,
    errorType: "policy",
  };
}
